import {
  Dispatch,
  Ref,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export function useIntersectionObserver<T extends HTMLElement>(
  observerCallback: (setIsLoading: Dispatch<SetStateAction<boolean>>) => void,
  hasMore: boolean,
  loading = false,
  delayMs = 100,
) {
  const [scrollY, setScrollY] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const targetElementRef = useRef<T | null>(null);

  useEffect(() => {
    if (!loading) {
      setIsLoading(false);
    }
  }, [loading]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (loading) return;
      const target = entries[0];
      setScrollY(target.intersectionRect.y);
      if (target.isIntersecting && hasMore && target.intersectionRect.y > 0) {
        setIsLoading(true);
        observerCallback?.(setIsLoading);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, hasMore],
  );

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const activate = () => {
      observer = new IntersectionObserver(handleObserver);
      if (targetElementRef.current) observer.observe(targetElementRef.current);
      else observer.disconnect();
    };

    if (delayMs > 0) {
      timeoutId = setTimeout(activate, delayMs);
    } else {
      activate();
    }

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [handleObserver, delayMs]);

  return {
    isLoading,
    scrollY,
    targetElementRef: targetElementRef as Ref<T | null>,
  };
}
